import { useEffect, useMemo, useState } from "react";
import { Box, Container } from "@mui/material";
import renderHTML from "react-render-html";
import { range, isNil } from "ramda";
import FitText from "react-textfit";

import { useTemplateGroup, useFaces, useRouter, useLoading, useScreen, useGtm, useUsers, useShares } from "hooks";
import CreateButton from "components/CreateButton";
import CastNumberSelect from "components/CastNumberSelect";
import PreviewVideoPlayer from "components/PreviewVideoPlayer";
import ProductMetaTags from "components/ProductMetaTags";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import { appRoutes } from "routes";
import MainTitle from "components/MainTitle";
import selectedCastNumber from "utils/selectedCastNumber";
import { trackTemplateView } from "tracking/google/analytics";

import {
  Root,
  MainInfo,
  Video,
  Main,
  Content,
  Title,
  TitlePlaceholder,
  FitTitle,
  Description,
  DescriptionPlaceholder,
  FitDescription,
  Actions,
  CastPlaceholder,
  ButtonPlaceholder,
  ButtonWrapper,
} from "./styles";

const TemplateGroup = () => {
  const { isMobile } = useScreen();
  const { loadTemplateGroup, templateGroup, clearTemplateGroup } = useTemplateGroup();
  const { trackVirtualPageView } = useGtm();
  const { resetDateToShare } = useShares();
  const { func: loadTemplateGroupWithLoader, isFinished: isFinishedTemplateGroup } = useLoading(loadTemplateGroup);
  const [isPromoTemplates, setIsPromoTemplates] = useState(false);
  const [isReadyToRender, setIsReadyToRender] = useState(false);

  const { loadFaces } = useFaces();

  const { func: loadFacesWithLoader, isFinished: isFinishedFaces } = useLoading(loadFaces);

  const {
    push,
    location,
    query: { slug },
  } = useRouter();

  const { loadCurrentUser } = useUsers();

  const { func: loadUserSession, isFinished: isUserSessionLoaded } = useLoading(loadCurrentUser);

  useEffect(() => {
    loadUserSession();
    resetDateToShare();
  }, []);

  useEffect(() => {
    if (!templateGroup || !isUserSessionLoaded) return;

    if (
      !TemplateGroupPresenter.isLive(templateGroup) ||
      !TemplateGroupPresenter.hasLiveTemplates(templateGroup) ||
      TemplateGroupPresenter.isInvite(templateGroup)
    ) {
      push({ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } });
      return;
    }

    trackVirtualPageView({
      name: "template-view-page",
      "template-short-name": templateGroup.name,
      "template-id": templateGroup.id,
    });

    const isPromo =
      TemplateGroupPresenter.id(templateGroup) === "ugly-sweater-song-sye" ||
      TemplateGroupPresenter.id(templateGroup) === "last-christmas-by-backstreet-boys-sye";
    setIsPromoTemplates(isPromo);

    trackTemplateView(templateGroup);
  }, [location, templateGroup, isUserSessionLoaded]);

  useEffect(() => {
    selectedCastNumber.clear();

    loadTemplateGroupWithLoader(slug).catch(() =>
      push({ pathname: appRoutes.page404Path(), state: { referrer: location.pathname } }),
    );
    loadFacesWithLoader();

    return () => clearTemplateGroup();
  }, [slug]);

  useEffect(() => {
    setIsReadyToRender(isFinishedTemplateGroup && isFinishedFaces);
  }, [isFinishedTemplateGroup, isFinishedFaces]);

  const isVertical = TemplateGroupPresenter.isStarringYouVertical(templateGroup);
  const title =
    !isNil(templateGroup) && TemplateGroupPresenter.templateGroupType(templateGroup).includes("clip")
      ? `${TemplateGroupPresenter.name(templateGroup)} Clip`
      : `${TemplateGroupPresenter.name(templateGroup)} Ecard`;

  const renderDescription = () => {
    let description = TemplateGroupPresenter.description(templateGroup);
    // eslint-disable-next-line default-case
    switch (TemplateGroupPresenter.id(templateGroup)) {
      case "ugly-sweater-song-sye":
        description = description.replace(
          /(Tipsy Elves|Click here)/gm,
          `<a href="https://www.tipsyelves.com/collections/jibjab" class="custom-description-link" target="_blank">$1</a>`,
        );
        description = description.replace(/\n/gm, `<br/>`);
        description = renderHTML(description);
        break;
      case "last-christmas-by-backstreet-boys-sye":
        description = description.replace(
          /(here)/gm,
          `<a href="https://backstreetboys.lnk.to/ChristmasAlbum!JJ" class="custom-description-link" target="_blank">here</a>`,
        );
        description = description.replace(/\n/gm, "<br/>");
        description = renderHTML(description);
        break;
    }
    return description;
  };

  const renderContent = useMemo(() => {
    if (!isReadyToRender)
      return (
        <Main isVertical={isVertical}>
          <Content isVertical={isVertical}>
            <TitlePlaceholder key="template-group-title" variant="text" align="center" />
            {range(0, 3).map((index) => (
              <DescriptionPlaceholder key={`template-group-description-${index}`} variant="text" />
            ))}
          </Content>
          <Actions>
            <CastPlaceholder variant="text" />
            <ButtonPlaceholder variant="rect" ratio="5/1" />
          </Actions>
        </Main>
      );

    return (
      <Main isVertical={isVertical}>
        <Content isVertical={isVertical}>
          <Title component="h1" variant="h2" align="center">
            {isMobile ? (
              <FitTitle>
                <FitText max={20}>{TemplateGroupPresenter.name(templateGroup)}</FitText>
              </FitTitle>
            ) : (
              <>{TemplateGroupPresenter.name(templateGroup)}</>
            )}
          </Title>
          <Description component="p">
            {isMobile ? (
              <FitDescription isPromoTemplates={isPromoTemplates}>
                <FitText max={16} min={15}>
                  {renderDescription()}
                </FitText>
              </FitDescription>
            ) : (
              <>{renderDescription()}</>
            )}
          </Description>
        </Content>
        <Actions>
          <Box sx={{ display: "block", mb: 2, mx: "auto", maxWidth: 220 }}>
            <CastNumberSelect templateGroup={templateGroup} fullWidth />
          </Box>
          <ButtonWrapper isVertical={isVertical}>
            <CreateButton loading={!isReadyToRender} buttonText="MAKE VIDEO" />
          </ButtonWrapper>
        </Actions>
      </Main>
    );
  }, [isPromoTemplates, isMobile, isReadyToRender, templateGroup, isVertical]);

  return (
    <Container>
      <Root>
        <MainInfo isVertical={isVertical}>
          <Video isVertical={isVertical}>
            <PreviewVideoPlayer loading={!isReadyToRender} templateGroup={templateGroup} />
          </Video>
          {renderContent}
        </MainInfo>
      </Root>
      {templateGroup && (
        <>
          <MainTitle title={title} />
          <ProductMetaTags product={templateGroup} presenter={TemplateGroupPresenter} />
        </>
      )}
    </Container>
  );
};

export default TemplateGroup;
